export interface CarouselSlide<T> {
  data: T;
  realIndex: number;
}

export interface CarouselBreakpoint {
  minWidth: number;
  items: number;
}

/**
 * Carrusel infinito basado en clones de borde (sin librería externa).
 * Solo soporta navegación por flechas (sin drag), como el owl-carousel-o que reemplaza.
 */
export class InfiniteCarousel<T> {

  itemsPerView = 1;
  currentIndex = 0;
  extended: CarouselSlide<T>[] = [];
  transitionEnabled = true;

  private items: T[] = [];
  private animating = false;
  private resetTimeout: any;
  private readonly transitionMs = 700;

  constructor(
    private readonly mobileBreakpoint = 400,
    private readonly mobileItemsPerView = 1,
    private readonly desktopItemsPerView = 2,
    // Para reemplazar carruseles owl con más de dos tramos responsive (ver
    // shortcuts.component.ts): lista de {minWidth, items} tipo "responsive" de owl, en vez de
    // el par mobile/desktop de arriba.
    private readonly breakpoints?: CarouselBreakpoint[]
  ) { }

  setItems(items: T[]): void {
    this.items = items || [];
    this.rebuild();
  }

  setViewportWidth(width: number): void {
    const next = this.breakpoints
      ? this.resolveItemsFromBreakpoints(width)
      : (width < this.mobileBreakpoint ? this.mobileItemsPerView : this.desktopItemsPerView);
    if (next !== this.itemsPerView) {
      this.itemsPerView = next;
      this.rebuild();
    }
  }

  get trackWidthPercent(): number {
    return this.itemsPerView > 0 && this.extended.length > 0
      ? (this.extended.length / this.itemsPerView) * 100
      : 100;
  }

  get slideWidthPercent(): number {
    return this.extended.length > 0 ? 100 / this.extended.length : 100;
  }

  get translatePercent(): number {
    return this.extended.length > 0 ? -(this.currentIndex * (100 / this.extended.length)) : 0;
  }

  next(): void {
    this.step(1);
  }

  prev(): void {
    this.step(-1);
  }

  private resolveItemsFromBreakpoints(width: number): number {
    const sorted = [...this.breakpoints].sort((a, b) => a.minWidth - b.minWidth);
    let items = sorted[0]?.items ?? 1;
    for (const breakpoint of sorted) {
      if (width >= breakpoint.minWidth) {
        items = breakpoint.items;
      }
    }
    return items;
  }

  private rebuild(): void {
    clearTimeout(this.resetTimeout);
    this.animating = false;
    this.transitionEnabled = false;

    const total = this.items.length;
    if (total === 0) {
      this.extended = [];
      this.currentIndex = 0;
      return;
    }

    const perView = Math.min(this.itemsPerView, total);
    const head = this.items.slice(-perView).map((data, i) => ({ data, realIndex: total - perView + i }));
    const body = this.items.map((data, realIndex) => ({ data, realIndex }));
    const tail = this.items.slice(0, perView).map((data, realIndex) => ({ data, realIndex }));

    this.extended = [...head, ...body, ...tail];
    this.currentIndex = perView;

    setTimeout(() => { this.transitionEnabled = true; }, 0);
  }

  private step(direction: 1 | -1): void {
    const total = this.items.length;
    if (total <= 1 || this.animating) {
      return;
    }

    this.animating = true;
    this.transitionEnabled = true;
    this.currentIndex += direction;

    clearTimeout(this.resetTimeout);
    this.resetTimeout = setTimeout(() => {
      const perView = Math.min(this.itemsPerView, total);
      if (this.currentIndex >= perView + total) {
        this.transitionEnabled = false;
        this.currentIndex -= total;
      } else if (this.currentIndex < perView) {
        this.transitionEnabled = false;
        this.currentIndex += total;
      }
      this.animating = false;
    }, this.transitionMs + 20);
  }
}
